import React, { useState,useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Box,
  Menu,
  MenuItem,
  Avatar,
  Divider,
  TextField,
  InputAdornment,
  useTheme,
  useMediaQuery,
  styled
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { auth } from '../utils/auth';
import instance from '../utils/axiosConfig';
import LDLOGO from '../assets/NewLogo.png';  // Adjust the path as needed
import { Analytics ,track} from "@vercel/analytics/react"
import CategoryIcon from '@mui/icons-material/Category';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import DevicesIcon from '@mui/icons-material/Devices';
import HomeIcon from '@mui/icons-material/Home';
import CheckroomIcon from '@mui/icons-material/Checkroom';
import ConstructionIcon from '@mui/icons-material/Construction';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import TextureIcon from '@mui/icons-material/Texture';
import InventoryIcon from '@mui/icons-material/Inventory';
import ScienceIcon from '@mui/icons-material/Science';
import AgricultureIcon from '@mui/icons-material/Agriculture';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import { CATEGORY_HIERARCHY } from '../constants/categories';

const DEFAULT_CATEGORIES = [
  'Alimentos',
  'Electrónica',
  'Bebidas Alcohólicas',
  'Celulares y Tablets',
];

// Add styled badge component
const PlanBadge = styled(Box)(({ planType }) => ({
  position: 'absolute',
  width: '40px',
  height: '40px',
  borderRadius: '50%',
  border: '2px solid',
  borderColor: planType === 'plata' ? '#C0C0C0' : 
              planType === 'oro' ? '#FFD700' : 
              planType === 'diamante' ? '#00FFFF' : '#FF8C00',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '&::before': {
    content: `"${planType.charAt(0).toUpperCase() + planType.slice(1)}"`,
    position: 'absolute',
    top: '-20px',
    fontSize: '0.7rem',
    color: planType === 'plata' ? '#C0C0C0' : 
           planType === 'oro' ? '#FFD700' : 
           planType === 'diamante' ? '#00FFFF' : 'transparent',
    fontWeight: 'bold'
  }
}));

// Add this helper function near the top of the file
const getRandomCategories = (categories, limit = 8) => {
  const entries = Object.entries(categories);
  const shuffled = [...entries].sort(() => 0.5 - Math.random());
  return Object.fromEntries(shuffled.slice(0, limit));
};

// Add this constant at the top with other constants
const MAIN_CATEGORIES = [
  "Alimentos",
  "Electrónica",
  "Hogar",
  "Construcción",
  "Automotor",
  "Industria",
  "Agricultura"
];

const NavBar = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchInput, setSearchInput] = useState('');
  
  // Profile Menu States
  const [anchorEl, setAnchorEl] = useState(null);
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState(null);
  
  // Filter Menu States
  const [categoryAnchor, setCategoryAnchor] = useState(null);
  const [priceAnchor, setPriceAnchor] = useState(null);
  const [locationAnchor, setLocationAnchor] = useState(null);
  const [popularCategories, setPopularCategories] = useState(DEFAULT_CATEGORIES);
  const isAuthenticated = auth.isAuthenticated();
  const userType = auth.getUserType();
  const userName = auth.getUserName();
  const user = auth.getUser();
  const [userPlan, setUserPlan] = useState(null);


  // Menu handlers
  const handleProfileMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMobileMenuOpen = (event) => setMobileMenuAnchor(event.currentTarget);
  const handleMenuClose = () => {
    setAnchorEl(null);
    setMobileMenuAnchor(null);
  };

  // Filter handlers
  const handleCategoryClick = (category) => {
    track('category_click', { 
      category,
      destination: category === 'Todos' ? '/s/' : `/s/?category=${category}`
    });
    navigate(category === 'Todos' ? '/s/' : `/s/?category=${category}`, { replace: true });
  };
  const handlePriceClick = (event) => setPriceAnchor(event.currentTarget);
  const handleLocationClick = (event) => setLocationAnchor(event.currentTarget);
  
  const handleFilterClose = () => {
    setCategoryAnchor(null);
    setPriceAnchor(null);
    setLocationAnchor(null);
  };

  const handleSearchSubmit = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const searchTerm = searchInput.trim();
      if (searchTerm) {
        track('search', { searchTerm });
        const newParams = new URLSearchParams(searchParams);
        newParams.set('search', searchTerm);
        setSearchParams(newParams);
        navigate(`/s/?search=${searchTerm}`, { replace: true });
      }
    }
  };

   // Add this useEffect to fetch popular categories
   useEffect(() => {
    const fetchPopularCategories = async () => {
      try {

        const response = await instance.get('/api/products/popular-categories/search?limit=4');
        
        // Only update if we got valid data
        if (response.data && response.data.length > 0) {
          setPopularCategories(response.data.map(cat => cat.category));
        }
      } catch (error) {
        console.error('[NavBar] Error fetching popular categories:', error);
        // Keep default categories on error
      }
    };

    fetchPopularCategories();
  }, []);

  const handleLogout = () => {
    handleMenuClose();
    track('user_logout');
    auth.logout();
    navigate('/login');
  };

  const handleLogoClick = () => {
    track('logo_click', { destination: '/' });
    navigate('/', { replace: true });
  };

  useEffect(() => {
    if (auth.isAuthenticated() && auth.isDistributor()) {
      const user = auth.getUser();
      if (user?.subscription?.planId) {
        setUserPlan(user.subscription.planId);
      }
    }
  }, []);

  const renderProfileMenu = (
    <Menu
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={handleMenuClose}
      keepMounted
      PaperProps={{
        elevation: 3,
        sx: {
          mt: 1.5,
          minWidth: 180,
          borderRadius: 2,
          '& .MuiMenuItem-root': {
            px: 2,
            py: 1,
            typography: 'body2',
            borderRadius: 1,
            marginX: 1,
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          },
        },
      }}
    >
      <MenuItem onClick={() => {
        handleMenuClose();
        track('profile_view');
        navigate('/profile');
      }}>
        Mi Perfil
      </MenuItem>
      
      {/* Show different options based on role */}
      {userType === 'admin' || userType === 'moderator' ? (
        <MenuItem onClick={() => {
          handleMenuClose();
          track('admin_panel_access', { userType });
          navigate(userType === 'moderator' ? '/moderator' : '/admin');
        }}>
          Panel de Control
        </MenuItem>
      ) : (userType === 'distributor' || userType === 'subUser') ? (
        <>
        <MenuItem onClick={() => {
          handleMenuClose();
          track('my_catalog_view', { userType });
          navigate(`/distributor/${user._id}`);
        }}>
          Mi Catálogo
        </MenuItem>
        <MenuItem onClick={() => {
          handleMenuClose();
          track('my_products_view', { userType });
          navigate('/my-products');
        }}>
          Mis Productos
        </MenuItem>
        </>
      ) : userType === 'business' && (
        <MenuItem onClick={() => {
          handleMenuClose();
          track('favorites_view');
          navigate('/favorite-products');
        }}>
          Productos Favoritos
        </MenuItem>
      )}

      <Divider sx={{ my: 1 }} />
      <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
        Cerrar Sesión
      </MenuItem>
    </Menu>
  );

  const renderMobileMenu = (
    <Menu
      anchorEl={mobileMenuAnchor}
      open={Boolean(mobileMenuAnchor)}
      onClose={handleMenuClose}
      PaperProps={{
        elevation: 3,
        sx: {
          mt: 1.5,
          minWidth: 180,
          borderRadius: 2,
        },
      }}
    >
      {/* Add general navigation options first */}
      <MenuItem onClick={() => {
        handleMenuClose();
        navigate('/about');
      }}>
        Sobre Nosotros
      </MenuItem>
      <MenuItem onClick={() => {
        handleMenuClose();
        navigate('/contact');
      }}>
        Contacto
      </MenuItem>
      <MenuItem onClick={() => {
        handleMenuClose();
        navigate('/pricing');
      }}>
        Planes y Precios
      </MenuItem>

      <Divider sx={{ my: 1 }} />

      {/* Then show auth-dependent options */}
      {!isAuthenticated ? (
        [
          <MenuItem key="login" onClick={() => {
            handleMenuClose();
            navigate('/login');
          }}>
            Iniciar sesión
          </MenuItem>,
          <MenuItem key="register-business" onClick={() => {
            handleMenuClose();
            navigate('/signup-business');
          }}>
            Ser comprador
          </MenuItem>,
          <MenuItem key="register-distributor" onClick={() => {
            handleMenuClose();
            navigate('/signup-distributor');
          }}>
            Ser vendedor
          </MenuItem>
        ]
      ) : (
        [
          <MenuItem key="profile" onClick={() => {
            handleMenuClose();
            navigate('/profile');
          }}>
            Mi Perfil
          </MenuItem>,
          
          // Show different options based on role
          userType === 'admin' || userType === 'moderator' ? (
            <MenuItem key="dashboard" onClick={() => {
              handleMenuClose();
              navigate(userType === 'moderator' ? '/moderator' : '/admin');
            }}>
              Panel de Control
            </MenuItem>
          ) : (userType === 'distributor' || userType === 'subUser') ? (
            <> 
            <MenuItem key="catalog" onClick={() => {
              handleMenuClose();
              navigate(`/distributor/${user._id}`);
            }}>
              Mi Catálogo
            </MenuItem>
            <MenuItem key="products" onClick={() => {
              handleMenuClose();
              navigate('/my-products');
            }}>
              Mis Productos
            </MenuItem>
            </>
          ) : userType === 'business' && [
            <MenuItem key="favorites" onClick={() => {
              handleMenuClose();
              navigate('/favorite-products');
            }}>
              Productos Favoritos
            </MenuItem>
          ],
          <MenuItem key="logout" onClick={handleLogout}>
            Cerrar Sesión
          </MenuItem>
        ]
      )}
    </Menu>
  );

  // Comment out the CategoryDropdown component but preserve it
  /*
  const CategoryDropdown = ({ category, subcategories, icon }) => {
    const navigate = useNavigate();

    return (
      <Box
        sx={{
          position: 'relative',
          '&:hover > .dropdown-content': {
            display: 'grid',
            opacity: 1
          },
        }}
      >
        <Button
          startIcon={icon}
          onClick={() => category === 'Ver más' ? null : navigate(`/s/?category=${category}`)}
          sx={{
            color: 'text.primary',
            textTransform: 'none',
            fontSize: '0.8rem',
            height:'100%'
          }}
        >
          {category}
        </Button>
        <Box
          className="dropdown-content"
          sx={{
            position: 'absolute',
            top: '100%',
            left: { md: '0', lg: '0' },
            right: { md: 'auto', lg: '0' },
            zIndex: 9999,
            backgroundColor: 'white',
            borderRadius: '4px',
            padding: '16px',
            display: 'none',
            opacity: 0,
            transition: 'opacity 0.2s ease-in-out',
            width: 'auto',
            minWidth: { md: '400px', lg: '600px' },
            maxWidth: '90vw',
            gridTemplateColumns: { 
              md: 'repeat(2, 1fr)',
              lg: 'repeat(3, 1fr)' 
            },
            gap: 1,
            maxHeight: '400px',
            overflowY: 'auto',
            transform: {
              md: 'translateX(-50%)',
              lg: 'none'
            },
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          }}
        >
          {Object.keys(subcategories).map((subCat) => (
            <MenuItem
              key={subCat}
              onClick={() => navigate(`/s/?category=${subCat}`)}
              sx={{
                fontSize: '0.8rem',
                py: 1.5,
                px: 2,
                whiteSpace: 'normal',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)'
                },
                color: 'black'
              }}
            >
              {subCat}
            </MenuItem>
          ))}
        </Box>
      </Box>
    );
  };
  */

  // Category icons mapping
  const categoryIcons = {
    "Alimentos": <RestaurantIcon />,
    "Electrónica": <DevicesIcon />,
    "Hogar": <HomeIcon />,
    "Indumentaria": <CheckroomIcon />,
    "Construcción": <ConstructionIcon />,
    "Automotor": <DirectionsCarIcon />,
    "Textiles": <TextureIcon />,
    "Papelería": <InventoryIcon />,
    "Industria": <ConstructionIcon />,
    "Químicos": <ScienceIcon />,
    "Agricultura": <AgricultureIcon />,
    "Embalajes": <LocalShippingIcon />
  };

  return (
    <>
    <Analytics />
      {/* First Navbar */}
      <AppBar position="static" sx={{ 
        backgroundColor: '#ffffff', 
        boxShadow: 'none',
      }}>
        <Toolbar sx={{ 
          justifyContent: 'center',
          px: { xs: 2, md: '15vw' },
          minHeight: '56px !important',
          height: '56px',
          gap: 4,
          '& .MuiButton-root': {
            fontSize: '0.8rem'
          },
          overflow: 'hidden'
        }}>
          {/* Logo - moved closer to center */}
          <Box
            component="img"
            src={LDLOGO}
            alt="Liderdist Logo"
            onClick={handleLogoClick}
            sx={{ 
              height: 55, 
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              marginRight: 0,
              flexShrink: 0,
              '&:hover': {
                opacity: 0.8
              },
              padding: '2px',
              borderRadius: '50px',
              backgroundColor: 'white',
            }}
          />

          {/* Center Menu Items */}
          <Box sx={{ 
            display: { xs: 'none', md: 'flex' }, 
            gap: 1,
            '& .MuiButton-root': {
              color: '#333',
              textTransform: 'none',
              minWidth: 'auto',
              px: 2,
              fontSize: '0.8rem',
              '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.04)'
              }
            }
          }}>
            {/* General navigation options */}
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button onClick={() => navigate('/about')}>
                Sobre Nosotros
              </Button>
              <Button onClick={() => navigate('/contact')}>
                Contacto
              </Button>
              <Button onClick={() => navigate('/pricing')}>
                Planes y Precios
              </Button>
            </Box>

            
          </Box>

          {/* Right Side Menu - User Options Box */}
          <Box sx={{ 
            display: { xs: 'none', md: 'flex' }, 
            gap: 2,
            marginLeft: 'auto',
            alignItems: 'center',
            paddingRight: 3,
            '& .MuiButton-root': {
              color: '#333',
              textTransform: 'none',
              fontSize: '0.8rem'
            }
          }}>
            {/* Account options */}
            {!isAuthenticated && (
              <Box sx={{ display: 'flex',alignItems: 'center', paddingRight: 3,marginLeft: 'auto', gap: 1, ml: 8 }}>
                <Button onClick={() => navigate('/login')}>
                  Iniciar Sesión
                </Button>
                <Button onClick={() => navigate('/signup')}>
                  Registrarse
                </Button>
              </Box>
            )}
            {isAuthenticated && (
              <>
                {userType === 'admin' || userType === 'moderator' ? (
                  <Button onClick={() => navigate(userType === 'moderator' ? '/moderator' : '/admin')}>
                    Panel de Control
                  </Button>
                ) : (userType === 'distributor' || userType === 'subUser') ? (
                  <>
                  <Button onClick={() => navigate(`/distributor/${user._id}`)}>
                    Mi Catálogo
                  </Button>
                  <Button onClick={() => navigate('/my-products')}>
                    Mis Productos
                  </Button>
                  </>
                ) : userType === 'business' && (
                  <>
                    <Button onClick={() => navigate('/favorite-products')}>
                      Productos Favoritos
                    </Button>
                  </>
                )}
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  gap: 1,
                  ml: 1,
                  position: 'relative'
                }}>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: '#333',
                      fontSize: '0.8rem'
                    }}
                  >
                    Hola, {userName}
                  </Typography>
                  <IconButton
                    onClick={handleProfileMenuOpen}
                    sx={{ 
                      position: 'relative',
                      '&:hover': {
                        backgroundColor: 'rgba(0,0,0,0.04)'
                      }
                    }}
                  >
                    {userPlan && (
                      <PlanBadge planType={userPlan} />
                    )}
                    {user?.profileImage ? (
                      <Avatar src={user.profileImage} sx={{ width: 32, height: 32 }} />
                    ) : (
                        <Avatar sx={{ width: 32, height: 32 }} />
                    )}
                  </IconButton>
                </Box>
              </>
            )}
          </Box>

          {/* Mobile Menu */}
          <Box sx={{ 
            display: { xs: 'flex', md: 'none' }, 
            position: 'absolute',
            right: 16
          }}>
            <IconButton
              color="inherit"
              onClick={handleMobileMenuOpen}
              sx={{ color: '#333' }}
            >
              <MenuIcon />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Second Navbar with Search and Categories */}
      <AppBar 
        position="static" 
        sx={{ 
          backgroundColor: '#ffffff',
          boxShadow: 'none',
          borderBottom: '1px solid #e0e0e0',
        }}
      >
        <Toolbar sx={{ 
          justifyContent: 'center',
          minHeight: '48px !important',
          px: { xs: 2, md: '15vw' },
        }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center',
            gap: 2,
            width: {xs:'100%',md:'auto'},
            justifyContent: 'flex-start'
          }}>
            <TextField
              size="small"
              placeholder="Buscar productos..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={handleSearchSubmit}
              sx={{
                backgroundColor: 'white',
                borderRadius: '4px',
                width: { xs: '100%', md: '300px' },
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: '#e0e0e0',
                  },
                  '&:hover fieldset': {
                    borderColor: '#bdbdbd',
                  },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
              }}
            />

            {!isMobile && (
              <Box 
                sx={{ 
                  display: 'flex', 
                  gap: 2, 
                  overflow: 'visible',
                  height: '48px',
                  alignItems: 'center'
                }}
              >
                {/* Original category dropdown implementation (commented out)
                {MAIN_CATEGORIES.map((category) => (
                  <CategoryDropdown
                    key={category}
                    category={category}
                    subcategories={CATEGORY_HIERARCHY[category].subcategories}
                  />
                ))}
                <CategoryDropdown
                  category="Ver más"
                  subcategories={Object.fromEntries(
                    Object.entries(CATEGORY_HIERARCHY)
                      .filter(([category]) => !MAIN_CATEGORIES.includes(category))
                  )}
                  icon={<KeyboardArrowDownIcon />}
                />
                */}

                {/* New implementation with top categories by product count */}
                {popularCategories.map((category) => (
                  <Button
                    key={category}
                    onClick={() => navigate(`/s/?category=${category}`)}
                    sx={{
                      color: 'text.primary',
                      textTransform: 'none',
                      fontSize: '0.8rem',
                      height: '100%',
                      '&:hover': {
                        backgroundColor: 'rgba(0,0,0,0.04)'
                      }
                    }}
                  >
                    {category}
                  </Button>
                ))}
              </Box>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      {renderProfileMenu}
      {renderMobileMenu}
      
    </>
  );
};

export default NavBar;
